import { useEffect, useMemo } from "react";
import { Tag } from "antd";
import useGet from "../common/useGet";
import useSearchOptions from "../common/useSearchOptions";
import {
  API_ENFORCEMENT_TEAMS,
  API_ENFORCEMENT_TEAMS_AVAILABLE,
  API_PARKINGS,
  API_PARKINGS_ALL,
} from "../../utils/apis";
import {
  CAN_VIEW_ALL_DEPLOYMENTS,
  CAN_VIEW_OWN_DEPLOYMENT,
  CAN_VIEW_SUPERVISED_DEPLOYMENTS,
} from "../../utils/constants";
import { addSelectItem, useGetUserPermissions } from "../../utils/helpers";
import { IsSuperUser } from "../../utils/permissions";

// ENUMS
// Role names the backend uses to decide what a user is deployed to
export const AGENT_GROUP = "Agent";
export const ENFORCEMENT_GROUP = "Enforcement";

export const ACCOUNT_TYPES = {
  [AGENT_GROUP]: "Agent",
  [ENFORCEMENT_GROUP]: "Enforcement Officer",
};

const TEAM_STATUSES = {
  active: "Active",
  deactivated: "Deactivated",
};

// Agent / Enforcement, or null when the user can't be deployed
export const getDeploymentGroup = (user: any) => {
  const roleName = user?.role_details?.[0]?.name;
  return roleName === AGENT_GROUP || roleName === ENFORCEMENT_GROUP
    ? roleName
    : null;
};

// A deployment carries either parking yards (Agent) or a team (Enforcement)
export const deploymentAccountType = (item: any) =>
  item?.enforcement_team
    ? ACCOUNT_TYPES[ENFORCEMENT_GROUP]
    : ACCOUNT_TYPES[AGENT_GROUP];

// PERMISSIONS
export const useDeploymentPermissions = () => {
  const permissions: string[] = useGetUserPermissions();
  const isSuperUser = IsSuperUser();
  return (permission: string) =>
    isSuperUser || permissions.includes(permission);
};

export const useCanViewDeployments = () => {
  const can = useDeploymentPermissions();
  return (
    can(CAN_VIEW_ALL_DEPLOYMENTS) ||
    can(CAN_VIEW_SUPERVISED_DEPLOYMENTS) ||
    can(CAN_VIEW_OWN_DEPLOYMENT)
  );
};

// TAGS
const TEAM_STATUS_COLORS: Record<string, string> = {
  active: "green",
  deactivated: "red",
};

export const ActiveTag = ({ active }: { active: boolean }) => {
  const status = active ? "active" : "deactivated";
  return (
    <Tag color={TEAM_STATUS_COLORS[status]} style={{ background: "none" }}>
      {TEAM_STATUSES[status]}
    </Tag>
  );
};

export const CapacityTag = ({ item }: { item: any }) => (
  <Tag
    color={item?.has_capacity ? "blue" : "orange"}
    style={{ background: "none" }}
  >
    {item?.current_member_count ?? 0} / {item?.capacity ?? 0}
    {item?.has_capacity ? "" : " (Full)"}
  </Tag>
);

// OPTION LOADERS
const yardOption = (item: any) =>
  addSelectItem({ label: item?.name, value: item?.id });

const teamOption = (item: any) =>
  addSelectItem({ label: item?.name, value: item?.id });

// { value: name } lookup, since deployments carry yard and team ids only
const toNames = (options: any[]) =>
  Object.fromEntries(options.map((item: any) => [item.value, item.name]));

export const useParkingYardOptions = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_PARKINGS_ALL });
  }, []);

  const options = useMemo(
    () => (Array.isArray(data) ? data : data?.results || []).map(yardOption),
    [data],
  );

  const names = useMemo(() => toNames(options), [options]);

  return { options, names, loading };
};

// Searches parking yards by name. Picked yards stay in the options so their
// tags keep a label while other results load.
export const useParkingYardSearch = (
  selected: string[] = [],
  names: Record<string, string> = {},
) => {
  const search = useSearchOptions({
    api: API_PARKINGS,
    searchParam: "name",
    params: { page_size: 50 },
    toOption: yardOption,
  });

  const missing = (selected || [])
    .filter((id) => !search.options.some((o: any) => o.value === id))
    .map((id) => yardOption({ id, name: names[id] ?? id }));

  return { ...search, options: [...missing, ...search.options] };
};

export const useEnforcementTeamOptions = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_ENFORCEMENT_TEAMS, params: { page_size: 50 } });
  }, []);

  const options = useMemo(
    () => (data?.results || data || []).map(teamOption),
    [data],
  );

  const names = useMemo(() => toNames(options), [options]);

  return { options, names, loading };
};

// Only teams with room left, so a full team is never selectable
export const useAvailableTeamOptions = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({
      api: API_ENFORCEMENT_TEAMS_AVAILABLE,
      params: { page_size: 50 },
    });
  }, []);

  const options = useMemo(
    () => (data?.results || data || []).map(teamOption),
    [data],
  );

  return { options, loading };
};
